import { get } from '@ember/object';
import Service, { service } from '@ember/service';
import { task, timeout } from 'ember-concurrency';
import fetch from 'fetch';
import type SessionService from 'invite-sign/services/session';
import { buildKioskPingUrl, KIOSK_PING_INTERVAL_MS, type KioskPingEvent } from 'invite-sign/utils/kiosk-ping';
import zft from 'invite-sign/utils/zero-for-tests';

export type KioskHeartbeatContext = {
  companyId: number | string;
  locationId: string;
};

export default class KioskHeartbeatService extends Service {
  @service declare session: SessionService;

  #started = false;

  start(context: KioskHeartbeatContext): void {
    const isLiveEnv = zft(1) > 0;

    if (this.#started || !isLiveEnv || !this.session.isSharedDevice) return;

    this.#started = true;
    void this.heartbeatTask.perform(context);
  }

  heartbeatTask = task({ drop: true }, async (context: KioskHeartbeatContext): Promise<void> => {
    this.ping('boot', context);

    for (;;) {
      await timeout(KIOSK_PING_INTERVAL_MS);
      this.ping('heartbeat', context);
    }
  });

  ping(event: KioskPingEvent, { companyId, locationId }: KioskHeartbeatContext): void {
    const token = this.session.sharedDeviceToken;

    if (!token) return;

    this.send(
      buildKioskPingUrl({
        event,
        companyId,
        locationId,
        token,
        kioskId: this.session.ensureKioskId(),
        setupAt: get(this.session.settings, 'setupAt'),
        uptimeSeconds: performance.now() / 1000,
      }),
    );
  }

  send(url: string): void {
    fetch(url, { cache: 'no-store', credentials: 'omit' }).catch(() => {});
  }
}
